import { TeacherRecord } from '@/shared/api/rest/services/teachers.service'

/** A teacher row from `GET /teachers`, enriched with a resolved branch name. */
export type RegistryTeacher = Omit<TeacherRecord, 'branch'> & {
    branch?: { _id?: string; name: string } | null
}

/**
 * `teachers.status` on the backend only ever holds three values: `'pending'`
 * (set on create), `'approved'` (set by `approve()`) and `'suspended'` (set by
 * `suspend()`). There is deliberately no "rejected" state — `reject()` hard-
 * deletes the row so the user can request the role again later.
 */
export type TeacherStatus = 'pending' | 'active' | 'suspended'

/**
 * Map the raw backend status onto a `TeacherStatus`. `'approved'` is surfaced
 * as "Active", which reads better in a registry; anything unrecognised falls
 * back to the same, since an approved teacher is the default resting state.
 */
export const normalizeStatus = (status?: string | null): TeacherStatus => {
    const value = status?.trim().toLowerCase()
    if (!value) return 'active'
    if (value.includes('pending')) return 'pending'
    if (value.includes('suspend')) return 'suspended'
    return 'active'
}

export const STATUS_LABEL: Record<TeacherStatus, string> = {
    pending: 'Pending',
    active: 'Active',
    suspended: 'Suspended'
}

/** Chakra `colorScheme` per status, shared by the list dot and the detail badge. */
export const STATUS_COLOR: Record<TeacherStatus, string> = {
    pending: 'orange',
    active: 'green',
    suspended: 'red'
}

export const getTeacherName = (teacher: RegistryTeacher) =>
    [teacher.firstName, teacher.lastName].filter(Boolean).join(' ').trim() ||
    'Unnamed teacher'
